import { createRoot } from 'react-dom/client';
import { humanizeApiError } from '@lapo/core';
import { App } from './App.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { installGlobalErrorHandlers } from './clientErrors.js';
import {
  CALLBACK_PATH,
  buildNativeCallbackUrl,
  deliverCallbackToOpener,
  depositBrokeredCallback,
  isBrokeredCallback,
  relayCallbackToDesktopScheme,
} from './auth/oauthRedirect.js';
import { API_URL } from './config.js';
import { isTauri } from './shell/shell.js';
import './styles.css';

// OAuth callback landing (WEB ONLY): the provider redirects the popup here with ?code=&state=. Forward
// it to the opener and close — never mount the app on this path. The desktop shell has no popup opener;
// there the callback arrives as an app.lapo.client:// deep link handled inside the running app, so this
// page-load path is never taken (guarded to make that explicit).
if (!isTauri() && window.location.pathname === CALLBACK_PATH) {
  if (!deliverCallbackToOpener()) {
    if (isBrokeredCallback(window.location.search)) {
      // Mobile SERVER-BROKERED link: don't try to hand the code back to the app (the Android deep-link
      // handoff is what failed). POST it straight to the server broker, which links the account; the
      // app is polling and will pick it up (and would anyway on its next refresh, even if killed).
      renderCallbackMessage('Connecting your account…', 'You can return to Lapo.');
      void depositBrokeredCallback(API_URL, window.location.search).then(
        () => renderCallbackMessage('Account connected', 'You can close this tab and return to Lapo.'),
        (e: Error) => renderCallbackMessage('Could not finish connecting', humanizeApiError(e)),
      );
    } else {
      // No opener + not brokered → desktop shell's system-browser landing. Render a tappable link BEFORE
      // triggering the auto-relay (Android Chrome can block a gesture-less scheme navigation), then fire
      // the auto-relay for the happy path.
      renderNativeCallbackRelay(buildNativeCallbackUrl(window.location.search));
      relayCallbackToDesktopScheme();
    }
  }
} else {
  // Note: not wrapped in StrictMode — the editor effect creates a TipTap/ProseMirror view bound to
  // a Y.Doc, which is not safe to mount/unmount/remount synchronously (StrictMode's dev double-invoke
  // would build two editors on the same node). The effect cleanup still destroys the editor normally.
  // UX-001: capture uncaught errors + unhandled rejections from the very first tick (before App's own
  // effect runs), and give the whole tree an outermost ErrorBoundary so a crash in the shell itself
  // renders a recovery card instead of a blank white page on the auto-updating desktop/mobile fleet.
  installGlobalErrorHandlers();
  const root = document.getElementById('root');
  if (!root) throw new Error('missing #root');
  createRoot(root).render(
    <ErrorBoundary label="app-root">
      <App />
    </ErrorBoundary>,
  );
}

/**
 * Bare landing page shown when the OAuth callback lands in a native shell's system browser. Shows a
 * "return to Lapo" link pointed at the app's deep-link URL as the manual fallback for when the
 * auto-relay is blocked (notably Android Chrome, which won't launch an app from a gesture-less
 * navigation). Built with DOM APIs since React isn't mounted on this path.
 */
function renderNativeCallbackRelay(url: string): void {
  document.body.textContent = '';
  const msg = document.createElement('p');
  msg.textContent = 'Sign-in complete — returning to Lapo…';
  const link = document.createElement('a');
  link.href = url;
  link.textContent = 'Tap here if Lapo doesn’t reopen automatically';
  link.style.display = 'inline-block';
  link.style.marginTop = '12px';
  document.body.append(msg, link);
}

/** Bare status page for the server-brokered mobile link callback (React isn't mounted on this path). */
function renderCallbackMessage(title: string, detail: string): void {
  document.body.textContent = '';
  const h = document.createElement('h2');
  h.textContent = title;
  const p = document.createElement('p');
  p.textContent = detail;
  document.body.append(h, p);
}
